import { format, fromUnixTime, parseISO } from "date-fns";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { Col, Row } from "react-styled-flexboxgrid";
import { toast } from "react-toastify";
import { ApiApplications } from "../../../../../../api/applications";
import { ApiWorkers } from "../../../../../../api/workers";
import Avatar from "../../../../../../components/Avatar";
import BackgroundBox from "../../../../../../components/BackgroundBox";
import Box from "../../../../../../components/Box";
import Button from "../../../../../../components/Button";
import ButtonGroup from "../../../../../../components/ButtonGroup";
import Divider from "../../../../../../components/Divider";
import ExpansionPanel from "../../../../../../components/ExpansionPanel";
import FormLabel from "../../../../../../components/FormLabel";
import Icon from "../../../../../../components/Icon";
import Input from "../../../../../../components/Input";
import List from "../../../../../../components/List";
import ListItem from "../../../../../../components/ListItem";
import Loader from "../../../../../../components/Loader";
import Modal from "../../../../../../components/Modal";
import Paper from "../../../../../../components/Paper";
import RatingStars from "../../../../../../components/RatingStars";
import Tags from "../../../../../../components/Tags";
import Typography from "../../../../../../components/Typography";
import FormConfirmDelete from "../../../../../../forms/FormConfirmDelete";
import FormDateFromTo from "../../../../../../forms/FromDateFromTo";
import FormWorker from "../../../../../../forms/FormWorker";
import { useApi } from "../../../../../../hooks/useApi";
import { useManage } from "../../../../../../hooks/useManage";
import useTheme from "../../../../../../hooks/useTheme";
import { paths } from "../../../../../../routes";
import { OPERATIONS, ROLES } from "../../../../../../utils/constans";
import {
    calcSum,
    getImageUrl,
    parseApplications,
    parseWorker,
    parseWorkerAdditional,
} from "../../../../../../utils/helpers";
import {
    generateTablePdf,
    parseDataToPDFBody,
} from "../../../../../../utils/pdf";
import { makeStyles } from "../../../../../../utils/styles";
import TableApplications from "./components/TableApplications";
import { ApiLanguages } from "../../../../../../api/languages";
import { ApiProfessions } from "../../../../../../api/professions";
import Switch from "../../../../../../components/Switch";
import Gallery from "../../../../../../components/Gallery";
import { StyledDocumentImage, StyledDocumentLink } from "./styled";

const useStyles = makeStyles((css) => ({
    avatar: css`
        margin-bottom: 20px;
    `,
    listBox: css`
        padding: 0 20px;
        border-radius: 10px;
        box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.16);
        margin-bottom: 20px;
    `,
    boxLeft: css`
        margin-right: 40px;
    `,
    boxRight: css`
        margin-left: 40px;
    `,
    boxBackground: css`
        margin-bottom: 20px;
    `,
    dividerTop: css`
        margin-top: 20px;
        margin-bottom: 30px;
    `,
    dividerBottom: css`
        margin-top: 30px;
        margin-bottom: 15px;
    `,
    cost: css`
        display: flex;
        align-items: flex-end;
        justify-content: flex-end;
    `,
    paperBottom: css`
        margin-top: 10px;
    `,
    tableTopDivider: css`
        margin: 15px 0;
    `,
    tableBottom: css`
        margin-top: 15px;
    `,
    dataPicker: css`
        margin-top: 30px;
        margin-right: 40px;
    `,
    fontOne: css`
        font-size: 14px;
        font-weight: 600;
        margin-bottom: 20px;
    `,
    fontTwo: css`
        font-size: 12px;
        line-height: 16px;
    `,
    fontThree: css`
        font-size: 18px;
        line-height: 18px;
        font-weight: 700;
        margin-left: 20px;
    `,
    fontFour: css`
        font-size: 14px;
        font-weight: 600;
        margin-top: 20px;
        margin-bottom: 20px;
    `,
    paperBottomTitle: css`
        margin-bottom: 20px;
    `,
    paperNoData: css`
        margin: 20px 0;
        text-align: center;
    `,
    tags: css`
        margin: 0 0 30px;
    `,
    switchWrapper: css`
        margin: 0 0 20px;
        display: flex;
        align-items: center;
        & > * {
            flex-shrink: 0;
        }
    `,
    customInput: css`
        margin: 0;
        width: auto;
    `,
    switch: css`
        margin: 0 10px 0 0;
    `,
    label: css`
        margin: 0 10px 0 0;
    `,
    agreementWrapper: css`
        display: flex;
    `,
    documentGallery: css`
        margin-bottom: 30px;
        & > div {
            display: flex;
            align-items: flex-start;
            margin: 0 -2px;
        }
    `
}));

const WorkersSingle = () => {
    const { t } = useTranslation()
    const styles = useStyles();
    const theme = useTheme();
    const params = useParams();
    const history = useHistory();

    const [{ operation }, setState] = useManage();

    const [date, setDate] = useState("");

    // Setup request
    const [
        { data: workerData, loading: workerLoading },
        getById,
    ] = useApi(ApiWorkers.getById, { manual: true });

    const [
        { data: workerAdditionalData },
        getAdditional,
    ] = useApi(ApiWorkers.getAdditional, { manual: true });

    const [{ loading: updateLoading }, updateWorker] = useApi(
        ApiWorkers.update,
        { manual: true }
    );

    const [{ loading: deleteLoading }, deleteWorker] = useApi(
        ApiWorkers.delete,
        { manual: true }
    );
    const [
        { data: applicationsData, loading: applicationsLoading },
        getApplicationsData,
    ] = useApi(ApiApplications.getByCandidate, { manual: true });

    const role = useSelector((state) => state.auth.user?.user?.role?.type);

    const { currency } = useSelector((state) => state.currency);

    useEffect(() => {
        const doFetch = async () => {
            try {
                await getById(params?.id);
                getAdditional({ user_candidate_id: params?.id });
            } catch (error) {
                history.push(paths.workers);
            }
        };
        doFetch();
    }, [params, getById, getAdditional, history]);

    // Memoize data
    const worker = useMemo(() => parseWorker(workerData), [workerData]);

    const workerAdditional = useMemo(
        () => parseWorkerAdditional(workerAdditionalData),
        [workerAdditionalData]
    );

    const applications = useMemo(
        () => applicationsData && parseApplications(applicationsData),
        [applicationsData]
    );

    const [{ data: languages, loading: languagesLoading }] = useApi(
        ApiLanguages.get
    );
    const [{ data: professions, loading: professionsLoading }] = useApi(
        ApiProfessions.get
    );

    const handleUpdateWorker = async (values) => {
        await updateWorker({
            ...values,
            ...{
                user_candidate_id: worker.id,
            },
        });
        await getById(params?.id);
        setState(OPERATIONS.READ);
    };
    const handleClickDelete = () => setState(OPERATIONS.DELETE);

    const handleClickCancelModal = () => setState(OPERATIONS.READ);

    const handleClickEdit = () => setState(OPERATIONS.EDIT);

    const handleSubmitDelete = async () => {
        const res = await deleteWorker(worker?.id);
        if (res?.data) {
            toast.success(t('Pracownik został usunięty'));
            history.push(paths.workers)
        }
    };

    const handleSubmit = ({ time_start_from, time_start_to }) => {
        const parsedData = {
            user_candidate_id: worker?.id,
            job_day_start: format(fromUnixTime(time_start_from), 'yyyyMMdd'),
            job_day_end: format(fromUnixTime(time_start_to), 'yyyyMMdd')
        }
        setDate(t('Zlecenia od {{startDate}} do {{endDate}}', { startDate: format(fromUnixTime(time_start_from), 'dd.MM.yyyy'), endDate: format(fromUnixTime(time_start_to), 'dd.MM.yyyy') }))
        getApplicationsData(parsedData);
    };

    const handleClickDownload = () => {
        const parsedData = parseDataToPDFBody(applications, (application) => [
            application.object_name,
            application.date,
            application.profession,
            application.hours,
            application.candidate_ranking,
            `${application.cost * application.hours} ${currency}`,
        ]);

        generateTablePdf({
            title: t("Zlecenia pracownika {{imie}}", { imie: `${worker?.firstName} ${worker?.lastName}` }),
            subtitle: date ? date : '',
            tableTitle: t('Lista obiektów'),
            table: {
                head: [
                    [
                        t('Obiekt'),
                        t('Data świadczenia pracy'),
                        ('Stanowisko'),
                        t('Godziny'),
                        t('Ocena ze zlecenia'),
                        t('Koszt brutto')
                    ]
                ],
                body: parsedData,
                foot: [
                    [
                        "",
                        "",
                        "",
                        calcSum(parsedData, (row) => row[3]),
                        "",
                        `${calcSum(parsedData, (row) =>
                            parseFloat(row[5].replace(` ${currency}`, "")) * parseFloat(row[3])
                        )} ${currency}`,
                    ],
                ],
            },
            filename: t("Zlecenia {{godnosc}}", { godnosc: `${worker?.firstName} ${worker?.lastName}` })
        });
    };

    useEffect(() => {
        if (worker?.id) {
            getApplicationsData({ user_candidate_id: worker.id });
        }
    }, [worker, getApplicationsData]);
    return (
        <>
            {worker && (
                <>
                    <Paper>
                        <Row middle="xs">
                            <Col xs={6}>
                                <Box customStyle={styles.boxLeft}>
                                    <Typography as="h1">
                                        {worker?.firstName} {worker?.lastName}
                                    </Typography>
                                </Box>
                            </Col>
                            <Col xs={6}>
                                <Box customStyle={styles.boxRight}>
                                    <Row middle="xs">
                                        <Col xs={5}>
                                            <RatingStars rate={worker.rating} />
                                        </Col>
                                        <Col xs={7}>
                                            <Box customStyle={styles.cost}>
                                                <Typography as="span" customStyle={styles.fontTwo}>
                                                    {t("Koszt dla obiektów")}
                                                </Typography>
                                                {workerAdditional && (
                                                    <Typography
                                                        as="span"
                                                        customStyle={
                                                            styles.fontThree
                                                        }
                                                    >
                                                        {
                                                            workerAdditional.placesCost
                                                        }{" "}
                                                        {currency}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Col>
                                    </Row>
                                </Box>
                            </Col>
                        </Row>
                        <Divider customStyle={styles.dividerTop} />
                        <Row>
                            <Col xs={6}>
                                <Box customStyle={styles.boxLeft}>
                                    <Box customStyle={styles.avatar}>
                                        <Avatar
                                            src={worker.avatarUrl}
                                            size="large"
                                        />
                                    </Box>
                                    <Input
                                        label={t("Imię")}
                                        value={worker.firstName}
                                        readonly
                                    />
                                    <Input
                                        label={t("Nazwisko")}
                                        value={worker.lastName}
                                        readonly
                                    />
                                    <Input
                                        label={t("PESEL")}
                                        value={worker.pesel}
                                        readonly
                                    />
                                    <Input
                                        label={t("Data urodzenia")}
                                        value={worker.birthDate}
                                        readonly
                                    />
                                    <Input
                                        label={t("Miejsce urodzenia")}
                                        value={worker.birthPlace}
                                        readonly
                                    />
                                    <Input
                                        label={t("Obywatelstwo")}
                                        value={worker.citizenship}
                                        readonly
                                    />
                                    <Typography customStyle={styles.fontOne}>{t("Dane finansowe")}</Typography>
                                    <Input
                                        label={t("Numer konta")}
                                        value={worker.bankNumber}
                                        readonly
                                    />
                                    <Input
                                        label={t("Kraj banku")}
                                        value={worker.bankCountry}
                                        readonly
                                    />
                                    <Typography customStyle={styles.fontOne}>{t("Zawody wykonywane przez pracownika")}</Typography>
                                    <Tags tags={worker.professions} customStyle={styles.tags} readonly  />

                                    {role === ROLES.SUPERADMIN && (
                                        <>
                                            <Typography customStyle={styles.fontOne}>{t("Dokumenty dostarczone przez pracownika")}</Typography>
                                            <Box customStyle={styles.documentGallery}>
                                                <Gallery
                                                    imageComponents={[worker.passport, worker.personalIDObverse, worker.personalIDReverse]
                                                        .filter((val) => !!val)
                                                        .map(({ url }, i) => (
                                                            <StyledDocumentLink key={i}>
                                                                <StyledDocumentImage src={getImageUrl(url)} />
                                                            </StyledDocumentLink>
                                                        ))}
                                                />
                                            </Box>
                                            <Box customStyle={styles.switchWrapper}>
                                                <Switch 
                                                    customStyle={styles.switch} 
                                                    name={'data-verified'} 
                                                    checked={!!worker.isActiveStudent} 
                                                /> 
                                                <Typography component="p" customStyle={styles.label}>{t("Aktywny student do")} </Typography>
                                                <Input 
                                                    customStyle={styles.customInput} 
                                                    value={worker.studentIdExpiryDate}
                                                    disabled
                                                />
                                            </Box>
                                            <Box customStyle={styles.switchWrapper}>
                                                <Switch 
                                                    customStyle={styles.switch} 
                                                    name={'data-verified'} 
                                                    checked={!!worker.conformityDeclarationAccepted} 
                                                />
                                                <Typography component="p">{t("Oświadczenie o poprawności dokumentów")}</Typography>
                                            </Box>
                                            <Box customStyle={styles.switchWrapper}>
                                                <Switch 
                                                    customStyle={styles.switch}
                                                    name={'data-verified'} 
                                                    checked={!!worker.dataVerified} 
                                                />
                                                <Typography component="p">
                                                    {`${t("Pracownik zweryfikowany (ostatnia aktualizacja profilu")} 
                                                    ${format(parseISO(worker.updated), "dd.MM.yyyy")})`}
                                                </Typography>
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            </Col>
                            <Col xs={6}>
                                <Box customStyle={styles.boxRight}>
                                    <BackgroundBox
                                        topText={t("Data ważności badań sanitaro-epidemiologicznych")}
                                        bottomText={worker.validityDate}
                                        customStyle={styles.boxBackground}
                                    />
                                    {
                                        workerAdditional && (
                                            <List customStyle={styles.listBox}>
                                                <ListItem>
                                                    <Typography>{t("Liczba zleceń/godzin")}</Typography>
                                                    <Typography>{worker.ordersNumberTotal}/{workerAdditional.ordersTimeTotal}</Typography>
                                                </ListItem>
                                                <ListItem>
                                                    <Typography>{t("Liczba nieobecności")}</Typography>
                                                    <Typography>{workerAdditional.absencesAmount}</Typography>
                                                </ListItem>
                                                <ListItem>
                                                    <Typography>{t("Liczba spóźnień")}</Typography>
                                                    <Typography>{workerAdditional.lateAmount}</Typography>
                                                </ListItem>
                                            </List>
                                        )
                                    }
                                    <Typography customStyle={styles.fontOne}>{t("Dane kontaktowe")}</Typography>
                                    <Input
                                        label={t("Adres email")}
                                        defaultValue={worker.email}
                                        readonly
                                    />
                                    <Row bottom="xs">
                                        <Col xs={12}>
                                            <FormLabel>
                                                {t("Telefon")}
                                            </FormLabel>
                                        </Col>
                                        <Col xs={2}>
                                            <Input
                                                value={
                                                    worker?.phonePrefix
                                                }
                                                readonly
                                            />
                                        </Col>
                                        <Col xs={10}>
                                            <Input
                                                value={
                                                    worker?.phoneNumber
                                                }
                                                readonly
                                            />
                                        </Col>
                                    </Row>
                                    <Typography customStyle={styles.fontOne}>{t("Adres zamieszkania")}</Typography>
                                    <Row bottom="xs">
                                        <Col xs={12}>
                                            <Input
                                                label={t("Ulica")}
                                                defaultValue={worker?.address?.street}
                                                readonly
                                            />
                                        </Col>
                                        <Col xs={7}>
                                            <Input
                                                label={t("Numer domu")}
                                                defaultValue={worker?.address?.houseNumber}
                                                readonly
                                            />
                                        </Col>
                                        <Col xs={5}>
                                            <Input
                                                label={t("Numer lokalu")}
                                                defaultValue={worker?.address?.apartmentNumber}
                                                readonly
                                            />
                                        </Col>
                                        <Col xs={4}>
                                            <Input
                                                label={t("Kod pocztowy")}
                                                defaultValue={worker?.address?.postcode}
                                                readonly
                                            />
                                        </Col>
                                        <Col xs={8}>
                                            <Input
                                                label={t("Miejscowość")}
                                                defaultValue={worker?.address?.town}
                                                readonly
                                            />
                                        </Col>
                                    </Row>
                                    <ExpansionPanel
                                        title={t("Adres zameldowania")}
                                    >
                                        <Row bottom="xs">
                                            <Col xs={12}>
                                                <Input
                                                    label={t("Ulica")}
                                                    defaultValue={worker?.registeredAddress?.street}
                                                    readonly
                                                />
                                            </Col>
                                            <Col xs={7}>
                                                <Input
                                                    label={t("Numer domu")}
                                                    defaultValue={worker?.registeredAddress?.houseNumber}
                                                    readonly
                                                />
                                            </Col>
                                            <Col xs={5}>
                                                <Input
                                                    label={t("Numer lokalu")}
                                                    defaultValue={worker?.registeredAddress?.apartmentNumber}
                                                    readonly
                                                />
                                            </Col>
                                            <Col xs={4}>
                                                <Input
                                                    label={t("Kod pocztowy")}
                                                    defaultValue={worker?.registeredAddress?.postcode}
                                                    readonly
                                                />
                                            </Col>
                                            <Col xs={8}>
                                                <Input
                                                    label={t("Miejscowość")}
                                                    defaultValue={worker?.registeredAddress?.town}
                                                    readonly
                                                />
                                            </Col>
                                        </Row>
                                    </ExpansionPanel>
                                    <ExpansionPanel
                                        title={t("Adres korespondencyjny")}
                                    >
                                        <Row bottom="xs">
                                            <Col xs={12}>
                                                <Input
                                                    label={t("Ulica")}
                                                    defaultValue={worker?.correspondenceAddress?.street}
                                                    readonly
                                                />
                                            </Col>
                                            <Col xs={7}>
                                                <Input
                                                    label={t("Numer domu")}
                                                    defaultValue={worker?.correspondenceAddress?.houseNumber}
                                                    readonly
                                                />
                                            </Col>
                                            <Col xs={5}>
                                                <Input
                                                    label={t("Numer lokalu")}
                                                    defaultValue={worker?.correspondenceAddress?.apartmentNumber}
                                                    readonly
                                                />
                                            </Col>
                                            <Col xs={4}>
                                                <Input
                                                    label={t("Kod pocztowy")}
                                                    defaultValue={worker?.correspondenceAddress?.postcode}
                                                    readonly
                                                />
                                            </Col>
                                            <Col xs={8}>
                                                <Input
                                                    label={t("Miejscowość")}
                                                    defaultValue={worker?.correspondenceAddress?.town}
                                                    readonly
                                                />
                                            </Col>
                                        </Row>
                                    </ExpansionPanel>
                                    <Typography customStyle={styles.fontFour}>{t("Znajomość języków pracownika")}</Typography>
                                    <Tags
                                        tags={worker.languages}
                                        readonly
                                    />
                                </Box>
                            </Col>
                        </Row>
                        <Divider customStyle={styles.dividerBottom} />
                        <Row between="xs">
                            <Col>
                                <ButtonGroup>
                                    {role === ROLES.SUPERADMIN && (
                                        <Button
                                            onClick={handleClickEdit}
                                            variant="text"
                                            startIcon={<Icon name="edit" />}
                                        >
                                            {t("Edytuj pracownika")}
                                        </Button>
                                    )}
                                    <Button
                                        variant="text"
                                        onClick={handleClickDelete}
                                        startIcon={
                                            <Icon
                                                name="delete"
                                                width={12}
                                                height={12}
                                                fill={theme.colors.purple}
                                            />
                                        }
                                    >
                                        {t("Usuń pracownika")}
                                    </Button>
                                </ButtonGroup>
                            </Col>
                            <Col>
                                <ButtonGroup>
                                    {role === ROLES.SUPERADMIN && (
                                        <Button
                                            to={`${paths.chat}/${worker?.user?.id}`}
                                            variant="text"
                                            startIcon={
                                                <Icon
                                                    name="chat"
                                                    width={12}
                                                    height={12}
                                                    fill={theme.colors.purple}
                                                />
                                            }
                                        >
                                            {t("Wyślij wiadomość")}
                                        </Button>
                                    )}
                                </ButtonGroup>
                            </Col>
                        </Row>
                    </Paper>
                    <Paper customStyle={styles.paperBottom}>
                        <Typography as="h1" customStyle={styles.paperBottomTitle}>{t("Zlecenia")}</Typography>
                        <Row>
                            <Col xs={7}>
                                <FormDateFromTo
                                    loading={applicationsLoading}
                                    onSubmit={handleSubmit}
                                />
                            </Col>
                        </Row>
                        <Divider customStyle={styles.tableTopDivider} />
                        {applications && applications.length > 0 && (
                            <>
                                <TableApplications
                                    applications={applications}
                                />
                                <Box customStyle={styles.tableBottom}>
                                    <Row end="xs">
                                        <Col>
                                            <ButtonGroup gutters={10}>
                                                <Button
                                                    variant="text"
                                                    startIcon={
                                                        <Icon
                                                            name="download"
                                                            width={12}
                                                            height={12}
                                                        />
                                                    }
                                                    onClick={
                                                        handleClickDownload
                                                    }
                                                >
                                                    {t("Pobierz raport")}
                                                </Button>
                                            </ButtonGroup>
                                        </Col>
                                    </Row>
                                </Box>
                            </>
                        )}
                        {(applications && applications.length === 0) && <Typography as="h1" customStyle={styles.paperNoData}>{t("Brak wyników")}</Typography>}
                    </Paper>
                </>
            )}
            <Modal open={operation === OPERATIONS.DELETE}>
                <FormConfirmDelete
                    loading={deleteLoading}
                    onSubmit={handleSubmitDelete}
                    onClickCancel={handleClickCancelModal}
                />
            </Modal>
            <Modal open={operation === OPERATIONS.EDIT}>
                {worker && (
                    <FormWorker
                        loading={
                            updateLoading ||
                            languagesLoading ||
                            professionsLoading
                        }
                        languages={languages}
                        professions={professions}
                        initialValues={{
                            first_name: worker.firstName,
                            last_name: worker.lastName,
                            national_id: worker.pesel,
                            dob: worker.birthDate,
                            birth_place: worker.birthPlace,
                            nationality: worker.citizenship,
                            bank_account_country: worker.bankCountry,
                            bank_account: worker.bankNumber,
                            health_book_expires: worker.validityDate,
                            email: worker.email,
                            phone: worker.phoneNumber,
                            phone_prefix: worker.phonePrefix,
                            address_live: worker.address
                                ? {
                                      flat: worker.address.apartmentNumber,
                                      number: worker.address.houseNumber,
                                      post_code: worker.address.postcode,
                                      street: worker.address.street,
                                      city: worker.address.town,
                                  }
                                : null,
                            address_post: worker.correspondenceAddress
                                ? {
                                      flat:
                                          worker.correspondenceAddress
                                              .apartmentNumber,
                                      number:
                                          worker.correspondenceAddress
                                              .houseNumber,
                                      post_code:
                                          worker.correspondenceAddress.postcode,
                                      street:
                                          worker.correspondenceAddress.street,
                                      city: worker.correspondenceAddress.town,
                                  }
                                : null,
                            address_registered: worker.registeredAddress
                                ? {
                                      flat: worker.address.apartmentNumber,
                                      number: worker.address.houseNumber,
                                      post_code: worker.address.postcode,
                                      street: worker.address.street,
                                      city: worker.address.town,
                                  }
                                : null,
                            languages: worker.languages,
                            professions: worker.professions,
                            // user_candidate_id: worker?.id,
                            workerAdditional: workerAdditional
                                ? workerAdditional
                                : {},
                        }}
                        onSubmit={handleUpdateWorker}
                        onClickCancel={handleClickCancelModal}
                    />
                )}
            </Modal>
            <Loader
                show={workerLoading || applicationsLoading || deleteLoading}
            />
        </>
    );
};

export default WorkersSingle;
